import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Loader2, Bot, User, Sparkles, Minimize2, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from 'react-markdown';

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content: "👋 Hey there! I'm **COSMOS**, your SOLR assistant. I'm here to help with:\n\n- 🧪 **Product info** & recommendations\n- 📦 **Orders** & shipping\n- 💫 **Subscriptions** & savings\n- ❓ Any questions!\n\nHow can I help you today?",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const streamChat = async (userMessage: string) => {
    const messagesToSend = messages
      .filter(m => m.id !== "welcome")
      .map(m => ({ role: m.role, content: m.content }));
    
    messagesToSend.push({ role: "user", content: userMessage });

    const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-ai`;
    
    try {
      const session = await supabase.auth.getSession();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      };
      
      if (session.data.session?.access_token) {
        headers["Authorization"] = `Bearer ${session.data.session.access_token}`;
      }

      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({ 
          messages: messagesToSend,
          conversationId 
        }),
      });

      const newConvId = resp.headers.get("X-Conversation-Id");
      if (newConvId && !conversationId) {
        setConversationId(newConvId);
      }

      if (!resp.ok) {
        if (resp.status === 429) {
          toast({
            title: "Just a moment",
            description: "I'm a bit busy right now. Please try again in a few seconds.",
            variant: "destructive",
          });
          return;
        }
        if (resp.status === 402) {
          toast({
            title: "Service temporarily unavailable",
            description: "Our AI assistant is taking a quick break. Please try again later.",
            variant: "destructive",
          });
          return;
        }
        throw new Error("Failed to get response");
      }

      if (!resp.body) throw new Error("No response body");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let textBuffer = "";
      let assistantMessage = "";
      let assistantMsgId = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        textBuffer += decoder.decode(value, { stream: true });

        let newlineIndex: number;
        while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
          let line = textBuffer.slice(0, newlineIndex);
          textBuffer = textBuffer.slice(newlineIndex + 1);

          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            const content = parsed.choices?.[0]?.delta?.content as string | undefined;
            
            if (content) {
              assistantMessage += content;
              
              setMessages(prev => {
                const lastMsg = prev[prev.length - 1];
                if (lastMsg?.role === "assistant" && lastMsg.id === assistantMsgId) {
                  return prev.map((m, i) =>
                    i === prev.length - 1
                      ? { ...m, content: assistantMessage }
                      : m
                  );
                } else {
                  assistantMsgId = `assistant-${Date.now()}`;
                  return [
                    ...prev,
                    {
                      id: assistantMsgId,
                      role: "assistant",
                      content: assistantMessage,
                      timestamp: new Date(),
                    },
                  ];
                }
              });
            }
          } catch (e) {
            textBuffer = line + "\n" + textBuffer;
            break;
          }
        }
      }
    } catch (error) {
      console.error("Chat error:", error);
      toast({
        title: "Connection issue",
        description: "Couldn't reach COSMOS. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isStreaming) return;

    const userMessage = input.trim();
    setInput("");
    
    const newUserMessage: Message = {
      id: `user-${Date.now()}`,
      role: "user",
      content: userMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, newUserMessage]);
    setIsStreaming(true);

    await streamChat(userMessage);
    
    setIsStreaming(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const quickReplies = [
    "What products do you have?",
    "Track my order",
    "Tell me about subscriptions",
    "How does shipping work?",
  ];

  return (
    <>
      {/* Chat Toggle Button — lifted on mobile so it sits above the bottom nav */}
      <div className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full bg-gradient-to-r from-solr-nebula-purple to-solr-electric-blue hover:shadow-lg hover:shadow-solr-nebula-purple/30 transition-all duration-300 ${isOpen ? 'scale-90' : 'scale-100 hover:scale-105'}`}
        >
          {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        </Button>
        {!isOpen && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse" />
        )}
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed z-50 bg-solr-space-darker/95 backdrop-blur-lg border border-solr-nebula-purple/30 rounded-2xl shadow-2xl shadow-solr-nebula-purple/20 flex flex-col transition-all duration-300 ${
            isExpanded 
              ? 'bottom-20 right-4 left-4 top-6 md:bottom-6 md:right-6 md:left-auto md:top-6 md:w-[600px] md:h-[calc(100vh-3rem)]' 
              : 'bottom-36 right-4 w-[calc(100vw-2rem)] max-w-sm h-[500px] max-h-[calc(100vh-12rem)] md:bottom-24 md:right-6 md:w-96 md:h-[550px] md:max-h-[calc(100vh-8rem)]'
          }`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-solr-nebula-purple to-solr-electric-blue p-4 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-yellow-300" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">COSMOS</h3>
                  <p className="text-xs text-white/80 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                    AI Assistant
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-white/80 hover:text-white hover:bg-white/10"
                >
                  {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"} animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                <div className={`flex items-start space-x-2 max-w-[85%] ${message.role === "user" ? "flex-row-reverse space-x-reverse" : ""}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.role === "user" 
                      ? "bg-solr-electric-blue" 
                      : "bg-gradient-to-br from-solr-nebula-purple to-solr-electric-blue"
                  }`}>
                    {message.role === "user" ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div className={`px-4 py-3 rounded-2xl text-sm ${
                    message.role === "user"
                      ? "bg-solr-electric-blue text-white rounded-br-md"
                      : "bg-solr-space-dark text-solr-starfield-white border border-solr-nebula-purple/20 rounded-bl-md"
                  }`}>
                    {message.role === "assistant" ? (
                      <div className="prose prose-sm prose-invert max-w-none">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc pl-4 mb-2 space-y-1">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 space-y-1">{children}</ol>,
                            li: ({ children }) => <li className="text-solr-starfield-white/90">{children}</li>,
                            strong: ({ children }) => <strong className="text-solr-nebula-purple font-semibold">{children}</strong>,
                            em: ({ children }) => <em className="text-solr-starfield-silver">{children}</em>,
                            a: ({ href, children }) => (
                              <a href={href} className="text-solr-electric-blue hover:underline" target="_blank" rel="noopener noreferrer">
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {message.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    )}
                    <p className={`text-xs mt-2 ${message.role === "user" ? "text-white/50" : "text-solr-starfield-silver/50"}`}>
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            
            {isStreaming && messages[messages.length - 1]?.role !== "assistant" && (
              <div className="flex justify-start animate-in fade-in duration-300">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-solr-nebula-purple to-solr-electric-blue flex items-center justify-center">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="bg-solr-space-dark border border-solr-nebula-purple/20 px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-2">
                    <div className="flex space-x-1">
                      <span className="w-2 h-2 bg-solr-nebula-purple rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-solr-nebula-purple rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-solr-nebula-purple rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={scrollRef} />
          </div>

          {/* Quick Replies */}
          {messages.length <= 1 && !isStreaming && (
            <div className="px-4 pb-2 flex-shrink-0">
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply) => (
                  <button
                    key={reply}
                    onClick={() => setInput(reply)}
                    className="text-xs px-3 py-1.5 bg-solr-nebula-purple/10 text-solr-nebula-purple border border-solr-nebula-purple/30 rounded-full hover:bg-solr-nebula-purple/20 transition-colors"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-solr-nebula-purple/20 flex-shrink-0">
            <div className="flex space-x-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                disabled={isStreaming}
                className="bg-solr-space-dark border-solr-nebula-purple/30 text-white placeholder-solr-starfield-silver/50 rounded-xl focus:ring-2 focus:ring-solr-nebula-purple/50"
              />
              <Button 
                onClick={handleSend} 
                disabled={!input.trim() || isStreaming}
                className="px-4 bg-gradient-to-r from-solr-nebula-purple to-solr-electric-blue hover:opacity-90 rounded-xl transition-all"
              >
                {isStreaming ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-center text-solr-starfield-silver/40 mt-2">
              Powered by COSMOS AI
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
